import { DownloadIcon } from "@chakra-ui/icons";
import { IconButton, useToast } from "@chakra-ui/react";
import { useLoader } from "components/Loader/loader";
import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

import {
  Box,
  Flex,
  Image,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
} from "@chakra-ui/react";

const Invoice = ({ invoiceData, handleDownloadPDF, invoiceRef }) => {
  if (!invoiceData) return <Text>No invoice data provided.</Text>;

  return (
    <Box
      maxW='3xl'
      mx='auto'
      p={6}
      borderWidth={1}
      shadow='lg'
      borderRadius='lg'
      bg='white'
      ref={invoiceRef}
    >
      {/* Header */}
      <Flex justify='space-between' align='center'>
        {invoiceData.logo && <Image src={invoiceData.logo} alt='Logo' h={12} />}
        <Text color='gray.600'>{invoiceData.date || "No date provided"}</Text>
      </Flex>

      <Text fontSize='2xl' fontWeight='bold' mt={4}>
        INVOICE #{invoiceData.invoiceNumber || "N/A"}
      </Text>

      {/* Invoice Details */}
      <Flex mt={6} justify='space-between'>
        <Box>
          <Text fontWeight='bold'>INVOICE BY</Text>
          <Text>{invoiceData.invoiceBy?.name || "N/A"}</Text>
          <Text>{invoiceData.invoiceBy?.address || "N/A"}</Text>
          <Text>{invoiceData.invoiceBy?.city || "N/A"}</Text>
          <Text>{invoiceData.invoiceBy?.country || "N/A"}</Text>
        </Box>
        <Box>
          <Text fontWeight='bold'>INVOICE TO</Text>
          <Text>{invoiceData.invoiceTo?.name || "N/A"}</Text>
          <Text>{invoiceData.invoiceTo?.address || "N/A"}</Text>
          <Text>{invoiceData.invoiceTo?.city || "N/A"}</Text>
          <Text>{invoiceData.invoiceTo?.country || "N/A"}</Text>
        </Box>
      </Flex>

      {/* Invoice Table */}
      <Table variant='simple' mt={6} borderTopWidth={1}>
        <Thead>
          <Tr>
            <Th textAlign='left'>DESCRIPTION</Th>
            <Th>UNIT</Th>
            <Th>PRICE ($)</Th>
            <Th>AMOUNT</Th>
          </Tr>
        </Thead>
        <Tbody>
          {invoiceData.items && invoiceData.items.length > 0 ? (
            invoiceData.items.map((item, index) => (
              <Tr key={index} borderBottomWidth={1}>
                <Td>{item.description || "N/A"}</Td>
                <Td textAlign='center'>{item.unit || 0}</Td>
                <Td textAlign='center'>${item.price?.toFixed(2) || "0.00"}</Td>
                <Td textAlign='center'>
                  ${((item.unit || 0) * (item.price || 0)).toFixed(2)}
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={4} textAlign='center'>
                No items available
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      {/* Totals */}
      <Box textAlign='right' mt={4}>
        <Text>SUBTOTAL: ${invoiceData.subtotal?.toFixed(2) || "0.00"}</Text>
        <Text>
          VAT ({invoiceData.vatRate || 0}%): $
          {invoiceData.vatAmount?.toFixed(2) || "0.00"}
        </Text>
        <Text fontSize='2xl' fontWeight='bold' color='blue.600'>
          TOTAL: ${invoiceData.total?.toFixed(2) || "0.00"}
        </Text>
      </Box>

      {/* Footer */}
      <Flex
        mt={6}
        borderTopWidth={1}
        pt={4}
        justify='space-between'
        color='gray.600'
      >
        <Box>
          <Text>INVOICE DATE: {invoiceData.invoiceDate || "N/A"}</Text>
          <Text>DUE DATE: {invoiceData.dueDate || "N/A"}</Text>
          <Text>INVOICE NUMBER: #{invoiceData.invoiceNumber || "N/A"}</Text>
        </Box>
        <Box>
          <Text>REF N°: {invoiceData.refNumber || "N/A"}</Text>
          <Text>COMPANY SIRET: {invoiceData.companySiret || "N/A"}</Text>
          <Text>COMPANY VAT N°: {invoiceData.companyVat || "N/A"}</Text>
        </Box>
      </Flex>

      {/* Download Button */}
      <Button colorScheme='blue' mt={6} onClick={handleDownloadPDF}>
        Download PDF
      </Button>
    </Box>
  );
};

const InvoiceGenerator = ({ data }) => {
  const { isLoading, setIsLoading } = useLoader();
  const toast = useToast();
  const invoiceRef = useRef();
  const [invoiceData, setInvoiceData] = useState(null); // State to store the generated invoice

  const handleDownloadPDF = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: "Invoice",
  });

  const handleGenerateInvoice = async () => {
    const data2 = {
      logo: "https://via.placeholder.com/150",
      date: new Date().toLocaleDateString(),
      invoiceNumber: "INV-123456",
      invoiceBy: {
        name: "John Doe",
        address: "123 Main St",
        city: "Springfield",
        country: "USA",
      },
      invoiceTo: {
        name: "Jane Doe",
        address: "456 Main St",
        city: "Springfield",
        country: "USA",
      },
      items: [
        {
          description: "Item 1",
          unit: 2,
          price: 250,
        },
        {
          description: "Item 2",
          unit: 1,
          price: 500,
        },
      ],
      subtotal: 1000,
      vatRate: 20,
      vatAmount: 200,
      total: 1200,
      invoiceDate: new Date().toLocaleDateString(),
      dueDate: new Date().toLocaleDateString(),
      refNumber: "REF-123456",
      companySiret: "123456789",
      companyVat: "987654321",
    };

    setIsLoading(true);
    setInvoiceData(data2); // Store generated invoice data in state
  };

  return (
    <Box>
      <IconButton
        aria-label='Download'
        icon={<DownloadIcon />}
        size='sm'
        colorScheme='teal'
        background={"transparent"}
        border={"1px solid"}
        borderColor='teal.500'
        mr={2}
        onClick={handleGenerateInvoice}
        disabled={isLoading}
      />
      {/* Conditionally render Invoice component when data is available */}
      {invoiceData && (
        <Invoice
          invoiceData={invoiceData}
          handleDownloadPDF={handleDownloadPDF}
          invoiceRef={invoiceRef}
        />
      )}
    </Box>
  );
};

export default InvoiceGenerator;
